import React, { useState, useEffect } from 'react';
import CalendarView from '../../components/common/CalendarView';
import Preloader from '../../components/common/Preloader';
import { getHolidays } from '../../services/adminService';
import { getMyLeaves } from '../../services/leaveService';

const EmployeeHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
        try {
            const hData = await getHolidays();
            const sortedH = hData.sort((a, b) => new Date(a.date) - new Date(b.date));
            setHolidays(sortedH);

            const lData = await getMyLeaves();
            const sortedL = lData.sort((a, b) => new Date(b.startDate) - new Date(a.startDate)); // newest first
            setLeaves(sortedL);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };
    fetchData();
  }, []);

  if (loading) return <Preloader />;

  return (
    <div className="ems-page-container">
      <div className="ems-max-width">
        <div className="ems-header-section">
          <div>
            <h1 className="ems-page-title">Company Holidays & Leaves</h1>
            <p className="ems-page-subtitle">View all upcoming holidays and your personal leave history.</p>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', marginTop: '1rem', alignItems: 'stretch' }}>
          
          {/* First Box: Calendar */}
          <div style={{ flex: '1 1 350px', maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>
            <CalendarView holidays={holidays} leaves={leaves} />
          </div>

          {/* Second Box: Total Holidays List */}
          <div style={{ flex: '1 1 300px', background: 'white', borderRadius: '16px', padding: '2rem', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '580px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
            <h2 style={{ fontSize: '1.25rem', color: '#111827', marginBottom: '1.5rem', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.75rem', fontWeight: '700', flexShrink: 0 }}>Total Holidays in Year</h2>
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {holidays.length === 0 ? (
                  <li style={{ color: '#6b7280' }}>No holidays found.</li>
                ) : (
                  holidays.map(h => {
                    const d = new Date(h.date);
                    const dateStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                    return (
                      <li key={h._id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6', color: h.status === 'withdrawn' ? '#9ca3af' : '#4b5563', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontWeight: '600', color: h.status === 'withdrawn' ? '#9ca3af' : '#1f2937', minWidth: '100px', textDecoration: h.status === 'withdrawn' ? 'line-through' : 'none' }}>{dateStr}</span>
                            <span style={{ margin: '0 0.75rem', color: '#9ca3af' }}>-</span>
                            <span style={{ fontWeight: '500', color: h.status === 'withdrawn' ? '#9ca3af' : '#374151', textDecoration: h.status === 'withdrawn' ? 'line-through' : 'none' }}>{h.name}</span>
                            {h.status === 'withdrawn' && (
                                <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', background: '#fee2e2', color: '#ef4444', fontWeight: '600', marginLeft: 'auto' }}>holiday withdrawned</span>
                            )}
                        </div>
                        {h.status === 'withdrawn' && (
                            <span style={{ fontSize: '0.85rem', color: '#ef4444', marginLeft: '125px', fontWeight: '500' }}>Reason: {h.withdrawnReason}</span>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>

          {/* Third Box: My Leaves List */}
          <div style={{ flex: '1 1 300px', background: 'white', borderRadius: '16px', padding: '2rem', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '580px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
            <h2 style={{ fontSize: '1.25rem', color: '#111827', marginBottom: '1.5rem', borderBottom: '1px solid #f3f4f6', paddingBottom: '0.75rem', fontWeight: '700', flexShrink: 0 }}>My Leaves</h2>
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {leaves.length === 0 ? (
                  <li style={{ color: '#6b7280' }}>No leaves found.</li>
                ) : (
                  leaves.map(l => {
                    const start = new Date(l.startDate);
                    const end = new Date(l.endDate);
                    const startStr = `${String(start.getDate()).padStart(2, '0')}/${String(start.getMonth() + 1).padStart(2, '0')}/${start.getFullYear()}`;
                    const endStr = `${String(end.getDate()).padStart(2, '0')}/${String(end.getMonth() + 1).padStart(2, '0')}/${end.getFullYear()}`;
                    const dateStr = startStr === endStr ? startStr : `${startStr} to ${endStr}`;
                    
                    let statusColor = '#9ca3af';
                    if(l.status === 'approved') statusColor = '#10b981';
                    if(l.status === 'rejected') statusColor = '#ef4444';
                    if(l.status === 'pending') statusColor = '#f59e0b';

                    return (
                      <li key={l._id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #f3f4f6', color: '#4b5563', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <span style={{ fontWeight: '600', color: '#1f2937' }}>{dateStr}</span>
                            <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '12px', background: `${statusColor}20`, color: statusColor, textTransform: 'capitalize', fontWeight: '600', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>{l.status}</span>
                          </div>
                          {l.reason && <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{l.reason}</span>}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EmployeeHolidays;
